import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { alarmApi, deviceApi, zoneApi } from '../services/api';
import logger from '../utils/logger';

export interface DashboardStats {
  totalDevices: number;
  onlineDevices: number;
  offlineDevices: number;
  criticalAlarms: number;
  warningAlarms: number;
  totalSites: number;
  totalZones: number;
}

export interface DashboardData {
  stats: DashboardStats;
  recentAlarms: any[];
  sites: any[];
  devices: any[];
}

async function fetchDashboardData(orgId?: string): Promise<DashboardData> {
  // Platform admin has no orgId — pass empty string so backend returns all devices
  const devices = await deviceApi.getDevices(orgId || '');

  const now = new Date();
  const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000);

  const onlineDevices = devices.filter((d: any) => {
    if (d.lastSeenAt) {
      const lastSeen = new Date(d.lastSeenAt.endsWith('Z') ? d.lastSeenAt : d.lastSeenAt + 'Z');
      return lastSeen > fifteenMinutesAgo;
    }
    if (d.status !== undefined) return d.status === 'online';
    return false;
  });

  const offlineDevices = devices.filter((d: any) => {
    if (d.lastSeenAt) {
      const lastSeen = new Date(d.lastSeenAt.endsWith('Z') ? d.lastSeenAt : d.lastSeenAt + 'Z');
      return lastSeen <= fifteenMinutesAgo;
    }
    if (d.status !== undefined) return d.status === 'offline';
    return true;
  });

  // For alarms & zones, orgId is required in the URL path.
  // Platform admin (no orgId): aggregate from unique orgIds found in devices, or skip gracefully.
  let alarms: any[] = [];
  let zonesData: any[] = [];

  if (orgId) {
    // Regular user — single org
    [alarms, zonesData] = await Promise.all([
      alarmApi.getActiveAlarms(orgId).catch(() => [] as any[]),
      zoneApi.getZones(orgId).catch(() => [] as any[]),
    ]);
  } else {
    // Platform admin — aggregate alarms from all orgs found in devices
    const uniqueOrgIds = [
      ...new Set(devices.map((d: any) => d.orgId as string).filter(Boolean)),
    ] as string[];
    const [allAlarms, allZones] = await Promise.all([
      Promise.all(uniqueOrgIds.map((oid: string) => alarmApi.getActiveAlarms(oid).catch(() => []))),
      Promise.all(uniqueOrgIds.map((oid: string) => zoneApi.getZones(oid).catch(() => []))),
    ]);
    alarms = allAlarms.flat();
    zonesData = allZones.flat();
  }

  const criticalAlarms = alarms.filter((a: any) => a.severity === 'critical');
  const warningAlarms = alarms.filter((a: any) => a.severity === 'warning');

  const sitesData = devices
    .filter((d: any) => d.site)
    .map((d: any) => d.site)
    .filter((v: any, i: number, a: any[]) => a.findIndex((t: any) => t.id === v.id) === i);

  return {
    stats: {
      totalDevices: devices.length,
      onlineDevices: onlineDevices.length,
      offlineDevices: offlineDevices.length,
      criticalAlarms: criticalAlarms.length,
      warningAlarms: warningAlarms.length,
      totalSites: sitesData.length,
      totalZones: zonesData.length,
    },
    recentAlarms: alarms.slice(0, 3),
    sites: sitesData,
    devices,
  };
}

export const dashboardKeys = {
  all: ['dashboard'] as const,
  byOrg: (orgId: string) => ['dashboard', orgId] as const,
};

export function useDashboardData(orgId: string | undefined) {
  return useQuery({
    queryKey: dashboardKeys.byOrg(orgId || '__platform__'),
    queryFn: () => fetchDashboardData(orgId),
    // 🛡️ Reduced from 60s to 120s — prevents JS thread contention with
    //    WebSocket real-time updates (crash F887917B). WS updates already
    //    invalidate the cache via useRealtimeAlarms/useRealtimeDeviceStatus.
    refetchInterval: 120_000,
    staleTime: 60_000, // 🛡️ Increased from 30s to 60s
  });
}

export function useInvalidateDashboard() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    logger.log('📊 Invalidating dashboard query');
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
  }, [queryClient]);
}
